import { html, css } from "lit";
import { customElement, state } from "lit/decorators.js";
import { PageElement } from "../lib/page-element.js";
import { shared, authStyles } from "../styles.js";
import { completeOnboarding, hasHandle } from "../api/account.js";
import { humanError } from "../api/errors.js";
import { tokenError } from "../lib/grammar.js";
import { navigate } from "../nav.js";

/**
 * page-welcome is the one-time onboarding step: a freshly verified account has no
 * handle yet, so this claims the permanent @handle (POST
 * /api/registry/onboarding/handle, which also provisions the personal team +
 * scope). The router routes handle-less accounts here; once a handle is set it
 * redirects home. Handle grammar is checked client-side before the request;
 * taken/reserved names surface via the {code,message} mapping.
 */
@customElement("page-welcome")
export class PageWelcome extends PageElement {
  static override styles = [
    shared,
    authStyles,
    css`
      .hint {
        margin: 4px 0 0;
        font-size: var(--fs-1);
      }
    `,
  ];

  @state() private handle = "";
  @state() private name = "";
  @state() private errorMsg = "";
  @state() private submitting = false;

  override firstUpdated() {
    this.setTitle("Choose your handle");
    super.firstUpdated();
    // Already onboarded (e.g. navigated here directly) → nothing to do.
    if (hasHandle()) navigate("/", { replace: true });
  }

  private async submit(e: Event) {
    e.preventDefault();
    const grammar = tokenError(this.handle.trim());
    if (grammar) {
      this.errorMsg = `Handle: ${grammar}`;
      return;
    }
    this.errorMsg = "";
    this.submitting = true;
    try {
      await completeOnboarding(this.handle.trim(), this.name.trim());
      navigate("/", { replace: true });
    } catch (err) {
      this.errorMsg = humanError(err);
    } finally {
      this.submitting = false;
    }
  }

  override render() {
    const grammar = this.handle ? tokenError(this.handle.trim()) : null;
    return html`
      <div class="auth-card">
        <div class="mark">
          <span class="glyph" aria-hidden="true">b</span>
          <span class="word">bino Registry</span>
        </div>
        ${this.errorMsg ? html`<div class="error" role="alert">${this.errorMsg}</div>` : ""}
        <form @submit=${this.submit}>
          <h1>Choose your handle</h1>
          <p class="lede">
            Your handle is your public identity: it becomes your personal
            <span class="mono">@handle</span> scope and team. It is lowercase and permanent.
          </p>
          <div class="field">
            <label for="handle">Handle</label>
            <input
              id="handle"
              class="mono"
              placeholder="jane_doe"
              autocomplete="off"
              spellcheck="false"
              .value=${this.handle}
              @input=${(e: Event) => (this.handle = (e.target as HTMLInputElement).value)}
              required
            />
            ${grammar
              ? html`<p class="error hint">${grammar}</p>`
              : html`<p class="muted hint">
                  You'll publish under <span class="mono">@${this.handle || "handle"}</span>.
                </p>`}
          </div>
          <div class="field">
            <label for="name">Display name <span class="muted">(optional)</span></label>
            <input
              id="name"
              .value=${this.name}
              @input=${(e: Event) => (this.name = (e.target as HTMLInputElement).value)}
            />
          </div>
          <div class="actions">
            <button class="btn btn-primary" type="submit" ?disabled=${this.submitting}>
              ${this.submitting ? "Setting up…" : "Continue"}
            </button>
          </div>
        </form>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "page-welcome": PageWelcome;
  }
}
